/**
 * Who is signed in, and what happens when they stop being.
 *
 * The session cookie is httpOnly, so nothing here can read it. `GET /auth/me` is
 * the only way to know, and a 401 from **any** request means the session ended —
 * so the redirect to the login screen carries the path the officer was on and
 * sends them back to it afterwards. Losing a session in the middle of a case
 * and landing on the queue is how work gets redone.
 */

import { createContext, useContext } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ApiFailure, api } from '../api/client'
import type { UserView } from '../api/client'

export interface Session {
  user: UserView | null
  loading: boolean
}

export const SessionContext = createContext<Session>({ user: null, loading: true })

export function useSession(): Session {
  return useContext(SessionContext)
}

export function useCurrentUser() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api.me(),
    retry: false,
    staleTime: 60_000,
  })
}

/** True when a failure means "sign in again" rather than "this is refused". */
export function isUnauthenticated(error: unknown): boolean {
  return error instanceof ApiFailure && error.status === 401
}

/** `/cases/abc?x=1` → `/login?next=%2Fcases%2Fabc%3Fx%3D1`. */
export function loginPathFor(location: { pathname: string; search: string }): string {
  const here = `${location.pathname}${location.search}`
  if (here === '/login' || here.startsWith('/login?')) return '/login'
  return `/login?next=${encodeURIComponent(here)}`
}

/**
 * Where to go after signing in. Only ever a path on this site: a `next` that
 * could name another origin is an open redirect, and this one arrives in a
 * query string.
 */
export function safeNext(raw: string | null): string {
  if (!raw) return '/cases'
  if (!raw.startsWith('/') || raw.startsWith('//')) return '/cases'
  return raw
}
